import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Wifi, WifiOff, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ServerUnavailableProps {
  onRetrySuccess: () => void;
}

export default function ServerUnavailable({ onRetrySuccess }: ServerUnavailableProps) {
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [connectionRestored, setConnectionRestored] = useState(false);

  const checkServerHealth = useCallback(async (): Promise<boolean> => {
    try {
      const response = await fetch("/api/health", {
        method: "GET",
        cache: "no-store",
      });
      return response.ok;
    } catch {
      return false;
    }
  }, []);

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    const isHealthy = await checkServerHealth();

    if (isHealthy) {
      setConnectionRestored(true);
      setTimeout(() => {
        onRetrySuccess();
      }, 1000);
    } else {
      setRetryCount((prev) => prev + 1);
      setCountdown(Math.min(10, 5 + retryCount));
    }
    setIsRetrying(false);
  }, [checkServerHealth, onRetrySuccess, retryCount]);

  useEffect(() => {
    if (countdown > 0 && !isRetrying && !connectionRestored) {
      const timer = setTimeout(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !isRetrying && !connectionRestored) {
      handleRetry();
    }
  }, [countdown, isRetrying, connectionRestored, handleRetry]);

  useEffect(() => {
    handleRetry();
    // Intentionally mount-only: trigger an initial health check exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (connectionRestored) {
    return (
      <div
        className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4"
        data-testid="server-restored-page"
      >
        <div className="max-w-md w-full text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center animate-bounce">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">We're Back!</h1>
          <p className="text-gray-600">Connection restored. Reloading the app...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center p-4"
      data-testid="server-unavailable-page"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <div className="flex justify-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center">
            {isRetrying ? (
              <RefreshCw className="w-10 h-10 text-blue-600 animate-spin" />
            ) : (
              <WifiOff className="w-10 h-10 text-blue-600" />
            )}
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-gray-900">
            {isRetrying ? "Connecting..." : "Updating TeamPulse"}
          </h1>
          <p className="text-gray-600">
            {isRetrying
              ? "Checking if the server is ready..."
              : "We're making things better! The app will be back in just a moment."}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 space-y-3">
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
            <Wifi className="w-4 h-4" />
            <span>
              {isRetrying ? "Attempting to reconnect..." : `Auto-retry in ${countdown} seconds`}
            </span>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-1000"
              style={{
                width: isRetrying
                  ? "100%"
                  : `${Math.max(0, Math.min(100, ((Math.min(10, 5 + retryCount) - countdown) / Math.min(10, 5 + retryCount)) * 100))}%`,
              }}
            />
          </div>

          {retryCount > 0 && <p className="text-xs text-gray-400">Retry attempt {retryCount}</p>}
        </div>

        <Button
          onClick={handleRetry}
          disabled={isRetrying}
          variant="outline"
          className="gap-2"
          data-testid="button-manual-retry"
        >
          <RefreshCw className={`w-4 h-4 ${isRetrying ? "animate-spin" : ""}`} />
          {isRetrying ? "Checking..." : "Retry Now"}
        </Button>

        <p className="text-xs text-gray-400">This usually takes less than 30 seconds</p>
      </div>
    </div>
  );
}
